
import { Trip, TransportationDetails } from '@/types/itinerary';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/auth';
import { useState, useCallback } from 'react';
import { buildEventDescription, getTypeDisplayName } from './utils/transportationUtils';

/**
 * Hook to handle saving transportation details to Supabase
 */
export const useSaveTransportation = (
  trip: Trip | null,
  onSuccessfulSave: () => void
) => {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Save transportation details to Supabase as an itinerary event
   */
  const saveTransportationToSupabase = useCallback(async (
    details: TransportationDetails,
    journeyType: string
  ): Promise<boolean> => {
    if (!trip?.id || !details.departureDate) {
      console.error("Cannot save transportation: missing trip ID or departure date");
      return false;
    }

    try {
      setIsSaving(true);
      console.log("Saving transportation details:", details);
      
      // Format date for database
      const formattedDate = format(new Date(details.departureDate), 'yyyy-MM-dd');
      const typeDisplay = getTypeDisplayName(details.type);
      const userName = details.userName || user?.email?.split('@')[0] || 'Anonymous';
      
      // Build description with all details
      const description = buildEventDescription(details);
      
      console.log("Inserting itinerary event with data:", {
        trip_id: trip.id,
        title: `${typeDisplay} to ${details.arrival}`,
        date: formattedDate,
        time: details.departureTime || null,
        location: `From: ${details.departure} To: ${details.arrival}`,
        description,
        is_transportation: true,
        transportation_type: details.type,
        user_name: userName
      });
      
      const { data, error } = await supabase
        .from('itinerary_events')
        .insert({
          trip_id: trip.id,
          title: `${typeDisplay} to ${details.arrival}`,
          date: formattedDate,
          time: details.departureTime || null,
          location: `From: ${details.departure} To: ${details.arrival}`,
          description: description,
          is_transportation: true,
          transportation_type: details.type,
          order_index: 0,
          created_by: user?.id || '00000000-0000-0000-0000-000000000000',
          user_name: userName
        })
        .select();
        
      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }
      
      console.log("Successfully saved transportation event:", data);
      toast.success(`${journeyType} travel details saved to itinerary`);
      onSuccessfulSave(); // Call the callback to refresh events
      return true;
    } catch (error) {
      console.error(`Error saving ${journeyType.toLowerCase()} details as event:`, error);
      toast.error('Failed to save travel details to itinerary');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [trip, user, onSuccessfulSave]);

  return {
    saveTransportationToSupabase,
    isSaving
  };
};
